/**
 * Text machinery for the typing tutor.
 *
 * Two views of the same target string are needed at once and they do NOT line
 * up one-to-one:
 *
 *   • keystrokes — one Unicode code point each. Typing "كَ" is two keystrokes:
 *     the kāf key, then Shift on the ḍād key for the fatḥa.
 *   • display    — one grapheme cluster each. "كَ" must render as a single unit,
 *     because a combining mark torn away from its letter renders as a stray
 *     dotted circle.
 *
 * So the cursor walks code points while the UI paints clusters, and a cluster
 * can be half-typed. Everything below exists to keep those two in step.
 */

/** Arabic combining marks: ḥarakāt, Qurʾānic annotations, dagger alif. */
const COMBINING = /[ً-ٰٟۖ-ۭ]/;

/** Canonical form. Authored content and typed input must be compared in NFC. */
export function normalize(text) {
  return (text || "").normalize("NFC");
}

/** The target as individual code points — one per keystroke. */
export function toCodePoints(text) {
  return Array.from(normalize(text));
}

/**
 * Group code points into grapheme clusters for rendering.
 * Returns [{ text, start, end }] where start/end index into toCodePoints().
 *
 * Uses Intl.Segmenter where available (every browser we target), and falls back
 * to "a combining mark joins the previous cluster", which is the only rule that
 * actually matters for Arabic.
 */
export function toClusters(text) {
  const points = toCodePoints(text);
  const clusters = [];

  if (typeof Intl !== "undefined" && typeof Intl.Segmenter === "function") {
    const segmenter = new Intl.Segmenter("ar", { granularity: "grapheme" });
    let start = 0;
    for (const { segment } of segmenter.segment(normalize(text))) {
      const length = Array.from(segment).length;
      clusters.push({ text: segment, start, end: start + length });
      start += length;
    }
    return clusters;
  }

  points.forEach((char, i) => {
    const previous = clusters[clusters.length - 1];
    if (previous && COMBINING.test(char)) {
      previous.text += char;
      previous.end = i + 1;
    } else {
      clusters.push({ text: char, start: i, end: i + 1 });
    }
  });
  return clusters;
}

export function isCombining(char) {
  return COMBINING.test(char);
}

/**
 * Which key produces `char`? Built once from the layout the API serves, so the
 * frontend never keeps its own copy of the mapping.
 *
 * Returns a { char -> {code, shift} } index. Ligature keys (لا and friends)
 * are indexed under their full two-code-point string as well as being
 * reachable letter by letter.
 */
export function buildCharIndex(layoutKeys) {
  const index = {};
  Object.entries(layoutKeys || {}).forEach(([code, key]) => {
    [true, false].forEach((shift) => {
      const char = shift ? key.shift : key.base;
      if (char) index[char] = { code, shift };
    });
  });
  return index;
}

/**
 * The key the learner should press next.
 *
 * `preferLigature` handles the one genuine ambiguity on this keyboard: lām+alef
 * can be produced either by the dedicated ligature key or by the two letter
 * keys in sequence, and the resulting text is byte-identical. Drills that exist
 * to teach the ligature key ask for it here; both routes are still accepted as
 * correct when the keystroke is judged.
 */
export function expectedKey(points, cursor, charIndex, preferLigature) {
  if (cursor >= points.length) return null;

  if (preferLigature && cursor + 1 < points.length) {
    const pair = points[cursor] + points[cursor + 1];
    const ligature = charIndex[pair];
    if (ligature) return { ...ligature, char: pair, span: 2 };
  }

  const char = points[cursor];
  const key = charIndex[char];
  return key ? { ...key, char, span: 1 } : null;
}

/** "KeyQ" / true -> "Shift+KeyQ", the token the stats API expects. */
export function keyToken(code, shift) {
  return shift ? `Shift+${code}` : code;
}

/**
 * Words-per-minute, using the standard "a word is five characters" convention
 * so the number is comparable to any other typing tutor.
 */
export function wordsPerMinute(correctCount, elapsedMs) {
  if (!elapsedMs || elapsedMs <= 0 || correctCount <= 0) return 0;
  return (correctCount / 5) / (elapsedMs / 60000);
}

export function accuracyPercent(correct, total) {
  if (!total) return 0;
  return (correct / total) * 100;
}

/**
 * A human label for a key, for the diagram and the "press this" hint.
 * Marks get a carrier so they render as a mark on a letter, not a lone circle.
 */
export function keyCapLabel(char) {
  if (!char) return "";
  return isCombining(char) ? `ـ${char}` : char; // tatweel as the carrier
}

/** True when a key has no visible glyph and needs to be named in words. */
export function isNamedKey(char) {
  return char === " ";
}

/** Readable name for a key with no glyph of its own. */
export function keyName(char) {
  return char === " " ? "Space" : char;
}
